import {
  FC,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  ChangeEvent,
} from 'react';
import { FormattedMessage } from 'react-intl';

import { useDetectOutsideClick } from 'hooks/useDetectOutsideClick';
import { useKeyEffect } from 'hooks/useKeyEffect';
import { getNextListIndex } from 'utils/array';

import Icon, { IconType } from 'components/UI/Icon';
import ArrowIcon from 'components/UI/Icon/ArrowIcon';

import {
  Container,
  SelectContainer,
  Select,
  SelectGroup,
  SelectInput,
  SelectLabel,
  SelectButton,
  Options,
  Option,
  Mark,
  Message,
  TagGrid,
} from './styled';
import Tag from '../Tag';

export type OptionType = {
  key: string;
  value: string;
  label: string;
  spec?: string;
};

type Props = {
  name: string;
  label: string;
  options: OptionType[];
  onSelect?: (value: string) => void;
  onMultiSelect?: (value: OptionType[]) => void;
  onInput?: (event: ChangeEvent<HTMLInputElement>) => void;
  register?: any;
  defaultValue?: string;
  value?: string;
  placeholder?: string;
  message?: string;
  disabled?: boolean;
  error?: any;
  enableSearch?: boolean;
  tight?: boolean;
  multiple?: boolean;
};

export const SelectField: FC<Props> = ({
  name,
  label,
  options,
  onSelect,
  onMultiSelect,
  onInput,
  register,
  placeholder,
  defaultValue,
  value,
  message,
  error,
  tight = false,
  enableSearch = false,
  disabled = false,
  multiple = false,
}) => {
  // Refs
  const selectRef = useRef<HTMLDivElement>(null);
  const floatInputRef = useRef<HTMLInputElement>(null);
  const placeholderRef = useRef<OptionType>({
    value: '',
    label: placeholder || '',
    key: '',
  });

  // State
  const [selected, setSelected] = useState<OptionType | null>(null);
  const [multipleSelected, setMultiSelected] = useState<OptionType[]>([]);

  const [highlighted, setHighlighted] = useState<OptionType | null>(null);
  const [searchState, setSearchState] = useState<string>('');

  // Hooks
  const [isOpen, setIsOpen] = useDetectOutsideClick([selectRef], false);

  // Full list
  const fullList = useMemo(() => {
    if (!placeholder) {
      return options;
    }
    return [placeholderRef.current, ...options];
  }, [options, placeholder]);

  // Actions
  const onOpen = useCallback(() => setIsOpen(true), [setIsOpen]);
  const onClose = useCallback(() => setIsOpen(false), [setIsOpen]);
  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchState(event.target.value);
  }, []);

  // Set default value after rendering component
  useEffect(() => {
    const defaultOption = fullList.find((item) => item.value === defaultValue);

    if (defaultOption) {
      setSelected(defaultOption);
    } else if (placeholder) {
      setSelected(placeholderRef.current);
    }
  }, [defaultValue, placeholder, fullList]);

  // Update value
  useEffect(() => {
    const found = fullList.find((item) => item.value === value);

    if (found) {
      setSelected(found);
    }
  }, [value, fullList]);

  // Set focus and search state when activated or deactivated
  useEffect(() => {
    if (!floatInputRef.current) {
      return;
    }

    if (!isOpen) {
      setSearchState('');
      floatInputRef.current.blur();
    } else {
      floatInputRef.current.focus();
    }
  }, [isOpen]);

  // Filtered list by search term
  const list = useMemo(() => {
    if (!searchState) {
      return fullList;
    }

    return fullList.filter((item) => {
      const lowerCaseLabel = item.label.toLowerCase();
      const lowerCaseSearch = searchState.toLowerCase();
      return lowerCaseLabel.includes(lowerCaseSearch);
    });
  }, [fullList, searchState]);

  // Set selected option, call onSelectValue and close dropdown
  const onSelectOption = useCallback(
    (option: OptionType | null) => {
      if (!option) {
        return null;
      }
      if (multiple && onMultiSelect) {
        const newMultiSelected = updateMultiSelected(option, multipleSelected);
        setMultiSelected(newMultiSelected);
        onMultiSelect(newMultiSelected);
      }
      if (onSelect) {
        setSelected(option);
        onSelect(option.value);
        onClose();
      }
    },
    [
      onClose,
      onSelect,
      setMultiSelected,
      multipleSelected,
      multiple,
      onMultiSelect,
    ],
  );

  // Highlight and scroll to the selected option's element
  const highlightOption = useCallback(
    (option: OptionType | null) => {
      if (!option) {
        return null;
      }
      setHighlighted(option);
      const highlightedElement = selectRef.current?.querySelector<HTMLElement>(
        `#${name}${option.key}`,
      );
      highlightedElement?.scrollIntoView({ block: 'nearest' });
    },
    [name],
  );

  // Handle keypresses within dropdown element
  const onEnter = useCallback(
    () => onSelectOption(highlighted),
    [highlighted, onSelectOption],
  );

  // On arrow click
  const onArrow = useCallback(
    (direction: 'up' | 'down') => (event: KeyboardEvent) => {
      event.preventDefault();

      const nextIndex = getNextListIndex(list, highlighted, direction === 'up');
      const nextOption = list[nextIndex];
      highlightOption(nextOption);
    },
    [highlightOption, highlighted, list],
  );

  // Keyboard navigation within dropdown
  useKeyEffect({
    action: onClose,
    addEventListener: isOpen,
    eventKeys: ['Escape', 'Tab'],
  });

  useKeyEffect({
    action: onEnter,
    addEventListener: isOpen,
    eventKeys: ['Enter'],
  });

  useKeyEffect({
    action: onArrow('down'),
    addEventListener: isOpen,
    eventKeys: ['ArrowDown'],
  });

  useKeyEffect({
    action: onArrow('up'),
    addEventListener: isOpen,
    eventKeys: ['ArrowUp'],
  });

  // Render option with highlight based on current search phrase
  const renderOptionLabel = useCallback(
    (optionLabel: string) => {
      if (!searchState) {
        return optionLabel;
      }

      const lowerCaseLabel = optionLabel.toLowerCase();
      const startIndex = lowerCaseLabel.indexOf(searchState);
      const endIndex = startIndex + searchState.length;

      return (
        <p>
          {optionLabel.substring(0, startIndex)}
          <Mark>{optionLabel.substring(startIndex, endIndex)}</Mark>
          {optionLabel.substring(endIndex)}
        </p>
      );
    },
    [searchState],
  );
  // Remove role
  const onRemoveItem = useCallback(
    (item: OptionType) => () => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const newArray = multipleSelected.filter((e, _i) => e.key !== item.key);
      setMultiSelected(newArray);
      if (multiple && onMultiSelect) {
        onMultiSelect(newArray);
      }
    },
    [setMultiSelected, multipleSelected, onMultiSelect, multiple],
  );
  // Empty list
  const emptyList = useMemo(() => {
    if (list.length) {
      return null;
    }
    return (
      <Option>
        <FormattedMessage id="select.no_match" />
      </Option>
    );
  }, [list]);

  const minimizeLabel = useMemo(() => {
    if (isOpen) {
      return enableSearch;
    }
    return selected !== null;
  }, [enableSearch, selected, isOpen]);

  // Message
  const messageText = useMemo(() => {
    if (error && message) {
      return `${message} (${error.message})`;
    }
    if (error) {
      return error.message;
    }
    if (message) {
      return message;
    }
    return null;
  }, [message, error]);

  // Error state
  const isError = useMemo(() => (error ? true : false), [error]);

  return (
    <Container>
      <SelectContainer $isTight={tight}>
        <Select
          $isOpen={isOpen}
          $isDisabled={disabled}
          $isError={isError}
          ref={selectRef}
        >
          <SelectGroup $isOpen={isOpen} $isDisabled={disabled}>
            <input type="hidden" readOnly autoComplete="off" {...register} />
            <SelectInput
              id={name}
              ref={floatInputRef}
              value={isOpen ? searchState : (selected?.label ?? '')}
              onChange={onChange}
              onInput={onInput}
              onFocus={onOpen}
              autoComplete="off"
              readOnly={!enableSearch}
              disabled={disabled}
              $isTight={tight}
            />
            <SelectLabel
              htmlFor={name}
              $isError={isError}
              $minimize={minimizeLabel}
              $isDisabled={disabled}
              $isTight={tight}
            >
              {label}
            </SelectLabel>
            <SelectButton
              type="button"
              $isDisabled={disabled}
              onClick={isOpen ? onClose : onOpen}
              tabIndex={-1}
            >
              <ArrowIcon
                direction={isOpen ? 'up' : 'down'}
                themeType={disabled ? 'grey4' : 'onBackground'}
              />
            </SelectButton>
          </SelectGroup>
          <Options $isOpen={isOpen}>
            {emptyList}
            {list.map((item) => (
              <Option
                key={item.key}
                id={`${name}${item.key}`}
                $isHighlighted={highlighted?.key === item.key}
                onClick={() => onSelectOption(item)}
              >
                {renderOptionLabel(item.label)}
                {(selected?.key === item.key ||
                  itemInList(item, multipleSelected)) && (
                  <Icon type={IconType.Check} themeType="primary" />
                )}
              </Option>
            ))}
          </Options>
        </Select>
      </SelectContainer>
      <Message $isHidden={tight} $isDisabled={disabled} $isError={isError}>
        {messageText}
      </Message>
      <TagGrid>
        {multipleSelected.map((selected) => (
          <Tag key={selected.value} onRemove={onRemoveItem(selected)}>
            {selected.value}
          </Tag>
        ))}
      </TagGrid>
    </Container>
  );
};

export default SelectField;

function updateMultiSelected(
  option: OptionType,
  multipleSelected: OptionType[],
): OptionType[] {
  if (!option.key || option.key === '') {
    return multipleSelected;
  }
  if (!itemInList(option, multipleSelected)) {
    return [...multipleSelected, option];
  } else {
    return multipleSelected.filter((item) => item.key !== option.key);
  }
}

function itemInList(option: OptionType, multipleSelected: OptionType[]) {
  return multipleSelected?.filter((item) => item.key === option.key).length > 0;
}
